import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  ActivityLogFilters,
  getActivityLogsRequest,
} from "../../api/activityLogApi";
import { ActivityLogResponse } from "../../models/ResponseModels/ActivityLogResponse";
import { PaginatedResponse } from "../../models/ResponseModels/PaginatedResponse";
import {
  ApiRejectPayload,
  getApiErrorPayload,
} from "../../utils/helperFunctions/apiErrorHelper";

interface ActivityLogsState {
  logs: ActivityLogResponse[];
  pagination: Omit<PaginatedResponse<ActivityLogResponse>, "data"> | null;
  filters: ActivityLogFilters;
  loading: boolean;
  error: string | null;
}

const initialState: ActivityLogsState = {
  logs: [],
  pagination: null,
  filters: {
    page: 1,
    per_page: 30,
    search: "",
    source: "",
    action: "",
  },
  loading: false,
  error: null,
};

export const fetchActivityLogs = createAsyncThunk(
  "activityLogs/fetchActivityLogs",
  async (filters: ActivityLogFilters, { rejectWithValue }) => {
    try {
      return await getActivityLogsRequest(filters);
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

const activityLogsSlice = createSlice({
  name: "activityLogs",
  initialState,
  reducers: {
    setActivityLogFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    clearActivityLogsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActivityLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActivityLogs.fulfilled, (state, action) => {
        const { data, ...pagination } = action.payload;

        state.loading = false;
        state.logs = data;
        state.pagination = pagination;
      })
      .addCase(fetchActivityLogs.rejected, (state, action) => {
        const payload = action.payload as ApiRejectPayload | undefined;

        state.loading = false;
        state.error = payload?.message || "Failed to load activity logs.";
      });
  },
});

export const { setActivityLogFilters, clearActivityLogsError } =
  activityLogsSlice.actions;
export default activityLogsSlice.reducer;
